import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiActivity, FiSmile, FiPhone, FiMapPin, FiClock, FiAward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RichTextRenderer from '../components/RichTextRenderer';
import HeroSection from '../components/HeroSection';
import { pageContent } from '../data/siteContent';
import dataService from '../services/dataService';

const TherapyDetail = () => {
  const { slug } = useParams();
  const [therapies, setTherapies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ref to prevent duplicate API calls in StrictMode
  const therapiesLoadedRef = useRef(false);

  useEffect(() => {
    const loadTherapies = async () => {
      if (therapiesLoadedRef.current) return;
      therapiesLoadedRef.current = true;
      
      try {
        const data = await dataService.getTherapies();
        setTherapies(data);
      } catch (error) {
        console.error('Failed to load therapies:', error);
        setTherapies([]);
      } finally {
        setLoading(false);
      }
    };

    loadTherapies();
  }, []);

  const content = pageContent[slug] || pageContent.default;
  const currentTherapy = therapies.find(t => t.path === `/therapy/${slug}`);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Related therapies (excluding current one)
  const relatedTherapies = therapies.filter(t => t.path !== `/therapy/${slug}`).slice(0, 4);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading therapy details...</p>
        </div>
      </div>
    );
  }

  if (!currentTherapy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center bg-gray-50 rounded-3xl p-12 shadow-2xl border border-gray-100/50 max-w-md mx-4"
        >
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiActivity} className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Therapy Not Found</h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            The therapy you're looking for doesn't exist or may have been moved.
          </p>
          <Link
            to="/therapies"
            className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <span>View All Therapies</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Custom Hero Section for Therapy Detail - Square Image */}
      <section className="relative pt-24 pb-16 lg:pt-8 lg:pb-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center lg:text-left"
            >
              {/* Badge */}
              {currentTherapy?.title && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100/80 text-emerald-700 text-sm font-medium mb-6 backdrop-blur-sm"
                >
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                  {currentTherapy.title}
                </motion.div>
              )}

              {/* Main Heading */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight"
              >
                {content.hero?.title || "Advanced Healing Therapy"}
              </motion.h1>

              {/* Subtitle */}
              {currentTherapy?.title && (
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.35 }}
                  className="text-xl sm:text-2xl text-emerald-700 font-semibold mb-4"
                >
                  {currentTherapy.title}
                </motion.h2>
              )}

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl"
              >
                {content.hero?.description || "Experience our specialized therapy designed to restore your health and well-being through evidence-based techniques."}
              </motion.p>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <a
                  href="tel:+919581556660"
                  className="inline-flex items-center justify-center px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all duration-300"
                >
                  <span>Book Consultation</span>
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-emerald-600 font-semibold rounded-xl border-2 border-emerald-200 hover:border-emerald-300 transition-all duration-300"
                >
                  <span>Learn More</span>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Image - Square, Clean */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 30 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative lg:order-last order-first"
            >
              <div className="relative bg-white">
                <img
                  src={currentTherapy?.image || content.hero?.image || '/images/theropy/default.webp'}
                  alt={currentTherapy?.title || content.hero?.title || "Therapy"}
                  className="w-80 h-80 object-cover mx-auto"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=600&fit=crop';
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3 space-y-6">
              <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-4">
                About This Therapy
              </div>
              <h2 className="text-3xl lg:text-5xl font-black text-gray-900 mb-6">
                {currentTherapy?.title || "Discover Natural Healing"}
              </h2>

              {/* Dynamic Description from Database */}
              {currentTherapy?.description ? (
                <div className="text-gray-700 text-lg leading-relaxed">
                  <RichTextRenderer content={currentTherapy.description} />
                </div>
              ) : (
                <div className="text-gray-600 italic">
                  <p>Therapy description will be available soon. Please contact us for more information.</p>
                </div>
              )}
            </div>
            
            {/* Quick Info Card */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-100 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-emerald-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Timings</p>
                      <p className="text-sm text-gray-600">10:00 AM - 9:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Call Us</p>
                      <a href="tel:+919581556660" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">9581556660</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiAward} className="w-5 h-5 text-emerald-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Experience</p>
                      <p className="text-sm text-gray-600">15+ Years Excellence</p>
                    </div>
                  </div>
                </div>
                <a href="tel:+919581556660" className="mt-6 w-full block text-center px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-colors">
                  Book Consultation
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      {content.benefits && (
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-4">
                Why Choose This
              </span>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">Proven Benefits</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Experience comprehensive healing for both body and mind
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Physical Benefits */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiActivity} className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Physical Benefits</h3>
                </div>
                <ul className="space-y-3">
                  {content.benefits.physical.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Emotional Benefits */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-100 hover:border-blue-300 transition-colors">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiSmile} className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Emotional & Lifestyle</h3>
                </div>
                <ul className="space-y-3">
                  {content.benefits.emotional.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Techniques Section */}
      {content.techniques && content.techniques.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-4">
                Our Approach
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Techniques We Use</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {content.techniques.map((tech, i) => (
                <div key={i} className="group flex items-start gap-4 p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white hover:from-emerald-50 hover:to-emerald-100/50 transition-all border-2 border-gray-100 hover:border-emerald-200 hover:shadow-lg">
                  <span className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 group-hover:bg-emerald-600 text-emerald-600 group-hover:text-white font-black text-xl transition-colors">
                    {i + 1}
                  </span>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{tech.name}</h4>
                    <p className="text-gray-600">{tech.purpose}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Therapies Section - Premium Redesign */}
      <section className="py-24 bg-gradient-to-b from-white via-emerald-50/30 to-white relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(16,185,129,0.3)_1px,transparent_0)] bg-[length:24px_24px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100/80 text-emerald-700 text-sm font-semibold mb-6 backdrop-blur-sm border border-emerald-200/50"
            >
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
              Explore More
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-black text-gray-900 mb-6 leading-tight"
            >
              Related Therapies
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed"
            >
              Discover complementary healing therapies to enhance your wellness journey
            </motion.p>
          </div>

          {/* Therapy Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {relatedTherapies.map((therapy, index) => (
              <motion.div
                key={therapy.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={therapy.path}
                  className="group block h-full bg-white rounded-3xl p-8 shadow-sm border border-gray-100/80 hover:shadow-xl hover:shadow-emerald-100/50 hover:border-emerald-200/60 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
                >
                  {/* Subtle gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>

                  {/* Therapy Icon/Image */}
                  <div className="relative z-10 mb-6">
                    <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-emerald-200/50 transition-all duration-500 group-hover:scale-110">
                      <img
                        src={therapy.image}
                        alt={therapy.title}
                        className="w-16 h-16 object-cover rounded-xl"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=200&h=200&fit=crop&crop=center';
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors duration-300 mb-3 leading-tight">
                      {therapy.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">
                      {therapy.tagline || "Experience holistic healing through traditional and modern therapeutic approaches."}
                    </p>

                    {/* Explore indicator */}
                    <div className="flex items-center justify-center gap-2 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300">
                      <span className="text-sm font-semibold">Explore</span>
                      <svg
                        className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Decorative element */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-emerald-100/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link
              to="/therapies"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <span>View All Therapies</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">Ready to Begin Your Healing Journey?</h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">
            Book a consultation with Dr. Saurabh Singh and experience world-class holistic care
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919581556660" className="px-8 py-4 bg-white text-emerald-900 hover:bg-emerald-50 rounded-xl font-bold transition-all shadow-xl flex items-center justify-center gap-2">
              <SafeIcon icon={FiPhone} />
              <span>Call 9581556660</span>
            </a>
            <Link to="/contact" className="px-8 py-4 bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-xl font-bold transition-all backdrop-blur-sm">
              Book Online
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TherapyDetail;